import { useState, useRef, useEffect } from 'react'

// ── Colour palette ─────────────────────────────────────────────────────────────
const SERVICE_COLORS = (() => {
  const tuned = [252, 200, 162, 32, 295, 105, 350, 235, 60, 178, 270, 18, 140, 218, 78, 320]
  return tuned.map(h => `oklch(0.62 0.13 ${h})`)
})()

export function colorFor(idx: number): string {
  return SERVICE_COLORS[idx % SERVICE_COLORS.length]
}

// ── Formatters ─────────────────────────────────────────────────────────────────
export function fmtNum(n: number | null | undefined, decimals = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—'
  const abs = Math.abs(n)
  if (abs >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
  if (abs >= 10)   return n.toFixed(decimals > 1 ? decimals : 1)
  if (abs >= 0.01) return n.toFixed(2)
  return n.toFixed(2)
}

export function fmtMoney(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  if (Math.abs(n) >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

export function fmtPct(n: number): string {
  if (!isFinite(n)) return '—'
  const v = n * 100
  if (Math.abs(v) >= 100) return v.toFixed(0) + '%'
  return v.toFixed(1) + '%'
}

export function fmtDate(s: string, opts: { short?: boolean } = {}): string {
  const parts = s.split('-').map(Number)
  const m = parts[1], d = parts[2]
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  if (opts.short) return `${d} ${months[m - 1]}`
  return `${months[m - 1]} ${d}`
}

// ── Sparkline ──────────────────────────────────────────────────────────────────
interface SparklineProps {
  values: number[]
  width?: number
  height?: number
  color?: string
  fill?: string
  strokeWidth?: number
  showDot?: boolean
}

export function Sparkline({
  values, width = 90, height = 28,
  color = 'currentColor', fill = 'none',
  strokeWidth = 1.4, showDot = false,
}: SparklineProps) {
  if (!values || values.length === 0) return null
  const max = Math.max(...values, 0.0001)
  const min = Math.min(...values, 0)
  const range = max - min || 1
  const xStep = values.length > 1 ? width / (values.length - 1) : 0
  const pts = values.map((v, i): [number, number] => [
    i * xStep,
    height - ((v - min) / range) * (height - 4) - 2,
  ])
  const d = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(2) + ' ' + p[1].toFixed(2)).join(' ')
  const dFill = `${d} L ${width} ${height} L 0 ${height} Z`
  const last = pts[pts.length - 1]
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      {fill !== 'none' && <path d={dFill} fill={fill} />}
      <path d={d} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round" />
      {showDot && <circle cx={last[0]} cy={last[1]} r="2" fill={color} />}
    </svg>
  )
}

// ── Chart tooltip ──────────────────────────────────────────────────────────────
interface TooltipRow { name: string; color: string; val: number }

interface ChartTooltipProps {
  x: number
  y: number
  date: string
  rows: TooltipRow[]
  total: number
  currency: string
}

function ChartTooltip({ x, y, date, rows, total, currency }: ChartTooltipProps) {
  return (
    <div className="drift-tooltip" style={{ left: x + 'px', top: y + 'px' }}>
      <div className="t-date">{fmtDate(date)}</div>
      {rows.map(r => (
        <div className="t-row" key={r.name}>
          <span className="t-name">
            <span style={{ display: 'inline-block', width: 8, height: 8, background: r.color, borderRadius: 2, marginRight: 6 }} />
            {r.name.length > 22 ? r.name.slice(0, 20) + '…' : r.name}
          </span>
          <span className="t-val">{fmtMoney(r.val)}</span>
        </div>
      ))}
      <div className="t-row t-total">
        <span className="t-name">Total</span>
        <span className="t-val">{fmtMoney(total)} {currency}</span>
      </div>
    </div>
  )
}

// ── Stacked area chart ─────────────────────────────────────────────────────────
export interface StackedSeriesItem {
  name: string
  color: string
  total: number
  daily: number[]
}

interface StackedAreaProps {
  series: StackedSeriesItem[]
  dates: string[]
  currency: string
  height?: number
  dimmed?: Set<string>
}

export function StackedArea({ series, dates, currency, height = 320, dimmed = new Set() }: StackedAreaProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<{ idx: number; x: number; y: number } | null>(null)
  const [w, setW] = useState(800)

  useEffect(() => {
    if (!ref.current) return
    const ro = new ResizeObserver(es => {
      for (const e of es) setW(Math.max(300, e.contentRect.width))
    })
    ro.observe(ref.current)
    return () => ro.disconnect()
  }, [])

  const padL = 50, padR = 12, padT = 14, padB = 28
  const innerW = w - padL - padR
  const innerH = height - padT - padB
  const n = dates.length

  const dailyTotals = new Array<number>(n).fill(0)
  for (let i = 0; i < n; i++) {
    for (const s of series) {
      if (!dimmed.has(s.name)) dailyTotals[i] += s.daily[i] ?? 0
    }
  }
  const maxY = Math.max(...dailyTotals, 0.01) * 1.08

  const xFor = (i: number) => padL + (n > 1 ? (i * innerW / (n - 1)) : innerW / 2)
  const yFor = (v: number) => padT + innerH - (v / maxY) * innerH

  const cumulative = new Array<number>(n).fill(0)
  const bands = series.map(s => {
    const isDim = dimmed.has(s.name)
    const top = new Array<number>(n)
    const bot = new Array<number>(n)
    for (let i = 0; i < n; i++) {
      bot[i] = cumulative[i]
      if (!isDim) cumulative[i] += s.daily[i] ?? 0
      top[i] = cumulative[i]
    }
    return { name: s.name, color: s.color, top, bot, isDim }
  })

  const pathFor = (top: number[], bot: number[]) => {
    let d = ''
    for (let i = 0; i < n; i++) d += (i === 0 ? 'M' : 'L') + xFor(i).toFixed(1) + ' ' + yFor(top[i]).toFixed(1) + ' '
    for (let i = n - 1; i >= 0; i--) d += 'L' + xFor(i).toFixed(1) + ' ' + yFor(bot[i]).toFixed(1) + ' '
    return d + 'Z'
  }
  const linePath = (arr: number[]) =>
    arr.map((v, i) => (i === 0 ? 'M' : 'L') + xFor(i).toFixed(1) + ' ' + yFor(v).toFixed(1)).join(' ')

  const ticks = [0, 1, 2, 3, 4].map(i => (maxY * i) / 4)

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    if (x < padL || x > w - padR) { setHover(null); return }
    let idx = Math.round(((x - padL) / innerW) * (n - 1))
    idx = Math.max(0, Math.min(n - 1, idx))
    setHover({ idx, x: xFor(idx), y: e.clientY - rect.top })
  }

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <svg width={w} height={height} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        {ticks.map((v, i) => (
          <g key={i}>
            <line x1={padL} y1={yFor(v)} x2={w - padR} y2={yFor(v)}
              stroke="var(--chart-grid)" strokeWidth="1" strokeDasharray={i === 0 ? '' : '2 3'} />
            <text x={padL - 6} y={yFor(v) + 3} fontSize="10" fill="var(--muted)"
              textAnchor="end" className="mono">{fmtNum(v)}</text>
          </g>
        ))}
        {bands.map(b => (
          <path key={b.name} d={pathFor(b.top, b.bot)} fill={b.color} opacity={b.isDim ? 0.06 : 0.85} />
        ))}
        <path d={linePath(dailyTotals)} fill="none" stroke="var(--ink)" strokeOpacity="0.25" strokeWidth="1" />
        {dates.map((d, i) => {
          if (!(n <= 8 || i % Math.ceil(n / 7) === 0 || i === n - 1)) return null
          return (
            <text key={i} x={xFor(i)} y={height - 8} fontSize="10" fill="var(--muted)"
              textAnchor="middle" className="mono">{fmtDate(d, { short: true })}</text>
          )
        })}
        {hover && (
          <>
            <line x1={hover.x} x2={hover.x} y1={padT} y2={padT + innerH}
              stroke="var(--ink)" strokeOpacity="0.25" strokeDasharray="2 3" />
            <circle cx={hover.x} cy={yFor(dailyTotals[hover.idx])} r="3"
              fill="var(--surface)" stroke="var(--ink)" strokeWidth="1.5" />
          </>
        )}
      </svg>
      {hover && (
        <ChartTooltip
          x={hover.x}
          y={yFor(dailyTotals[hover.idx])}
          date={dates[hover.idx]}
          rows={series
            .map(s => ({ name: s.name, color: s.color, val: s.daily[hover?.idx ?? 0] ?? 0, dim: dimmed.has(s.name) }))
            .filter(r => !r.dim && r.val > 0.001)
            .sort((a, b) => b.val - a.val)
            .slice(0, 6)}
          total={dailyTotals[hover.idx]}
          currency={currency}
        />
      )}
    </div>
  )
}

// ── Total area (single-series, used in drill panel) ────────────────────────────
interface TotalAreaProps {
  daily: number[]
  dates: string[]
  currency: string
  height?: number
}

export function TotalArea({ daily, dates, currency, height = 200 }: TotalAreaProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [w, setW] = useState(480)
  const [hover, setHover] = useState<{ idx: number; x: number; y: number } | null>(null)

  useEffect(() => {
    if (!ref.current) return
    const ro = new ResizeObserver(es => {
      for (const e of es) setW(Math.max(300, e.contentRect.width))
    })
    ro.observe(ref.current)
    return () => ro.disconnect()
  }, [])

  const padL = 50, padR = 12, padT = 14, padB = 28
  const innerW = w - padL - padR
  const innerH = height - padT - padB
  const n = dates.length
  const maxY = Math.max(...daily, 0.01) * 1.1
  const xFor = (i: number) => padL + (n > 1 ? i * innerW / (n - 1) : innerW / 2)
  const yFor = (v: number) => padT + innerH - (v / maxY) * innerH
  const dLine = daily.map((v, i) => (i === 0 ? 'M' : 'L') + xFor(i) + ' ' + yFor(v)).join(' ')
  const dFill = `${dLine} L ${xFor(n - 1)} ${padT + innerH} L ${xFor(0)} ${padT + innerH} Z`
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => maxY * t)

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    if (x < padL || x > w - padR) { setHover(null); return }
    let idx = Math.round(((x - padL) / innerW) * (n - 1))
    idx = Math.max(0, Math.min(n - 1, idx))
    setHover({ idx, x: xFor(idx), y: yFor(daily[idx]) })
  }

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <svg width={w} height={height} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id="dp-area-grad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"   stopColor="var(--accent)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {ticks.map((v, i) => (
          <g key={i}>
            <line x1={padL} x2={w - padR} y1={yFor(v)} y2={yFor(v)}
              stroke="var(--chart-grid)" strokeDasharray={i === 0 ? '' : '2 3'} />
            <text x={padL - 6} y={yFor(v) + 3} fontSize="10" fill="var(--muted)"
              textAnchor="end" className="dp-mono">{fmtNum(v)}</text>
          </g>
        ))}
        <path d={dFill} fill="url(#dp-area-grad)" />
        <path d={dLine} fill="none" stroke="var(--accent)" strokeWidth="1.6" />
        {daily.map((v, i) => (
          <circle key={i} cx={xFor(i)} cy={yFor(v)} r="2.2" fill="var(--accent)" />
        ))}
        {hover && (
          <>
            <line x1={hover.x} x2={hover.x} y1={padT} y2={padT + innerH}
              stroke="var(--ink)" strokeOpacity="0.25" strokeDasharray="2 3" />
            <circle cx={hover.x} cy={hover.y} r="4"
              fill="var(--surface)" stroke="var(--accent)" strokeWidth="1.6" />
          </>
        )}
        {dates.map((d, i) => {
          if (!(n <= 8 || i % Math.ceil(n / 7) === 0 || i === n - 1)) return null
          return (
            <text key={i} x={xFor(i)} y={height - 8} fontSize="10" fill="var(--muted)"
              textAnchor="middle" className="dp-mono">{fmtDate(d, { short: true })}</text>
          )
        })}
      </svg>
      {hover && (
        <div className="drift-tooltip" style={{ left: hover.x + 'px', top: hover.y + 'px' }}>
          <div className="t-date">{fmtDate(dates[hover.idx])}</div>
          <div className="t-row t-total">
            <span className="t-name">Daily spend</span>
            <span className="t-val">{fmtMoney(daily[hover.idx])} {currency}</span>
          </div>
        </div>
      )}
    </div>
  )
}
